/* eslint-disable no-void */
import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { queryClient } from 'utils/react-query';
import {
  getSessionPlan,
  sessionPlanKeys,
  useCreateSessionPlan,
  useGetSessionPlanStatus,
} from './useSessionPlanApi';

const STORAGE_KEY = 'session-plan-run';

type UseSessionPlanGenerationResult = {
  generate: (userInput: string) => void;
  /** Set when generation finishes; use to navigate to the detail page. */
  completedPlanId: string | null;
  isLoading: boolean;
  isPolling: boolean;
  progress: number;
  progressMessage: string;
  error: string | null;
  reset: () => void;
};

const clearStoredRun = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'localStorage',
      message: 'Failed to remove session-plan-run',
      level: 'error',
      data: { error },
    });
    Sentry.captureException(error);
  }
};

const storeRun = (runId: string): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ runId, startedAt: Date.now() }));
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'localStorage',
      message: 'Failed to set session-plan-run',
      level: 'error',
      data: { error },
    });
    Sentry.captureException(error);
  }
};

export const useSessionPlanGeneration = (): UseSessionPlanGenerationResult => {
  const [completedPlanId, setCompletedPlanId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const isFetchingPlanRef = useRef(false);

  const createMutation = useCreateSessionPlan();
  const statusMutation = useGetSessionPlanStatus();

  const reset = useCallback((): void => {
    setCompletedPlanId(null);
    setIsPolling(false);
    setProgress(0);
    setProgressMessage('');
    setError(null);
    setCurrentRunId(null);
    isFetchingPlanRef.current = false;
    clearStoredRun();
  }, []);

  const completeWithPlanId = useCallback(async (planId: string): Promise<void> => {
    if (isFetchingPlanRef.current) return;
    isFetchingPlanRef.current = true;
    try {
      await getSessionPlan(planId);
      await queryClient.invalidateQueries({ queryKey: sessionPlanKeys.list() });
      setCompletedPlanId(planId);
      setProgress(100);
      setProgressMessage('Unterrichtsplanung fertig!');
      setIsPolling(false);
      setCurrentRunId(null);
      clearStoredRun();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Die Unterrichtsplanung konnte nicht geladen werden'
      );
      setIsPolling(false);
      setCurrentRunId(null);
      clearStoredRun();
      Sentry.captureException(err);
    } finally {
      isFetchingPlanRef.current = false;
    }
  }, []);

  const pollRunStatus = useCallback(
    async (runId: string): Promise<void> => {
      if (statusMutation.isPending) return;
      try {
        const result = await statusMutation.mutateAsync(runId);
        setProgress(result.progress);
        setProgressMessage(result.progressMessage);

        if (result.status === 'FAILED') {
          setError(
            result.failureReason || 'Die Erstellung der Unterrichtsplanung ist fehlgeschlagen'
          );
          setIsPolling(false);
          setCurrentRunId(null);
          clearStoredRun();
          return;
        }

        if (result.status === 'COMPLETED') {
          if (result.resultId) {
            await completeWithPlanId(result.resultId);
          } else {
            setError('Die Erstellung ist abgeschlossen, aber kein Plan wurde zurückgegeben');
            setIsPolling(false);
            setCurrentRunId(null);
            clearStoredRun();
          }
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Der Fortschritt konnte nicht abgerufen werden'
        );
        setIsPolling(false);
        setCurrentRunId(null);
        clearStoredRun();
        Sentry.captureException(err);
      }
    },
    [statusMutation, completeWithPlanId]
  );

  useEffect(() => {
    if (!isPolling || !currentRunId) return;

    const interval = setInterval(() => {
      void pollRunStatus(currentRunId);
    }, 4000);

    return () => {
      clearInterval(interval);
    };
  }, [isPolling, currentRunId, pollRunStatus]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed: { runId: string } = JSON.parse(raw);
      if (parsed?.runId) {
        setCurrentRunId(parsed.runId);
        setIsPolling(true);
        setProgress((prev) => (prev > 0 ? prev : 5));
        setProgressMessage((prev) => prev || 'Fortschritt wird geladen...');
        void pollRunStatus(parsed.runId);
      }
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'localStorage',
        message: 'Failed to resume session-plan-run',
        level: 'error',
        data: { error: err },
      });
      Sentry.captureException(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resume once on mount
  }, []);

  const generate = useCallback(
    async (userInput: string): Promise<void> => {
      reset();
      try {
        const { runId } = await createMutation.mutateAsync(userInput);
        setCurrentRunId(runId);
        setProgress(0);
        setProgressMessage('Anfrage wird analysiert...');
        setIsPolling(true);
        storeRun(runId);
        void pollRunStatus(runId);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Die Erstellung der Unterrichtsplanung konnte nicht gestartet werden'
        );
        Sentry.captureException(err);
      }
    },
    [createMutation, reset, pollRunStatus]
  );

  return {
    generate,
    completedPlanId,
    isLoading: createMutation.isPending || statusMutation.isPending,
    isPolling,
    progress,
    progressMessage,
    error,
    reset,
  };
};
